import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Toaster } from "sonner";

import { UnauthorizedError, request } from "src/api/client";
import { keys } from "src/api/hooks";
import {
  Badge,
  Button,
  Card,
  FloatingPanel,
  cn,
  humanPopulation,
  humanRole
} from "src/components/ui";
import { ViewerDocument } from "src/graphql/generated";
import { AdminUsers } from "src/screens/admin-users";
import { AddOrgPerson } from "src/screens/add-org-person";
import { BulkImport } from "src/screens/bulk-import";
import { Departments } from "src/screens/departments";
import { CreateIdentity } from "src/screens/create-identity";
import { IdentityDetail } from "src/screens/identity-detail";
import { INITIAL_PEOPLE_VIEW, type PeopleView, Identities } from "src/screens/identities";
import { Login } from "src/screens/login";
import { OktaExport } from "src/screens/okta-export";
import { INITIAL_ORGS_VIEW, type OrgsView, PartnerOrgs } from "src/screens/partner-orgs";

/**
 * Adding a person is not among these, deliberately: it is an action, and these are
 * destinations. The controls that start it sit on the screens where somebody notices
 * a person is missing — the People list, and each firm's row on Organizations — and
 * the form they open floats over whatever is on screen rather than replacing it. See
 * `adding` below for why the panel is mounted here and not on either of them.
 */
type Tab = "people" | "orgs" | "departments" | "import" | "okta" | "access";

/** The People action creates an identity; the organization action links an existing one. */
type AddingPerson = { atOrganization: { id: string; name: string } | null };

export function App() {
  const [tab, setTab] = useState<Tab>("people");
  const [openId, setOpenId] = useState<string | null>(null);
  // Owned here rather than by either screen that opens it. The form has to outlive
  // opening somebody from the table — checking how a comparable person was entered is
  // the reason it floats at all — and a panel mounted inside Identities unmounts the
  // moment `openId` swaps that screen for a detail page, taking the draft with it.
  const [adding, setAdding] = useState<AddingPerson | null>(null);
  // Where the Organizations screen was left: its filter, and whose roster was open.
  // Owned here for the reason the create panel is — opening somebody unmounts the
  // screen, and state owned inside it does not survive the trip. The difference is
  // that this one is not a draft but a place, so Back returns to the row the operator
  // was working through rather than to the top of a collapsed table. See `OrgsView`.
  const [orgsView, setOrgsView] = useState<OrgsView>(INITIAL_ORGS_VIEW);
  // Keep the People filters and page across the same trip to a person and back.
  const [peopleView, setPeopleView] = useState<PeopleView>(INITIAL_PEOPLE_VIEW);

  // The viewer query is also the session check: a 401 here means log in, and it is
  // the only place that decision is made.
  const { data, isPending, error } = useQuery({
    queryKey: keys.viewer,
    queryFn: () => request(ViewerDocument),
    retry: false
  });

  if (isPending) {
    return <Shell>Loading…</Shell>;
  }
  if (error instanceof UnauthorizedError) {
    return <Login />;
  }
  if (error || !data) {
    return (
      <Shell>
        <Card title="Something went wrong">
          <p className="text-sm text-slate-700">{error?.message ?? "no viewer"}</p>
        </Card>
      </Shell>
    );
  }

  const viewer = data.viewer;
  const isAdmin = viewer.roles.includes("IAM_ADMIN");

  const tabs: Array<{ id: Tab; label: string; show: boolean }> = [
    { id: "people", label: "People", show: true },
    // Readable by anyone: the picker on the create form already shows these, so
    // there is nothing here a reader cannot see. Writing is gated inside.
    { id: "orgs", label: "Organizations", show: true },
    { id: "departments", label: "Departments", show: true },
    // iam-admin for the same reason Console access is, and not because it writes:
    // matching a sheet's rows reads both populations at once, so a caller holding one
    // would duplicate people it cannot see. The `wantGlobal` table in
    // internal/auth/role_test.go is what keeps "only iam-admin" true of
    // `run_bulk_import`.
    { id: "import", label: "Bulk import", show: isAdmin },
    // Same gate as bulk import: the export reads both populations, so a writer of
    // one would push an incomplete directory. `run_okta_export` is the real check.
    { id: "okta", label: "Okta export", show: isAdmin },
    { id: "access", label: "Console access", show: isAdmin }
  ];

  return (
    <Shell
      header={
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-slate-600">{viewer.email}</span>
          {viewer.roles.map((role) => (
            <Badge key={role}>{humanRole(role)}</Badge>
          ))}
          {/* Labelled, and per population, because the bare list of names said
              neither what it was nor how much access it represented: it listed the
              populations the caller can *read*, so someone who reads both and writes
              one saw exactly what an iam-admin sees. */}
          <span className="text-xs text-slate-500">
            Access:{" "}
            {viewer.readablePopulations
              .map(
                (population) =>
                  `${humanPopulation(population)} (${
                    viewer.writablePopulations.includes(population) ? "edit" : "view"
                  })`
              )
              .join(" · ")}
          </span>
          {viewer.isDevBypass ? (
            // Worth saying loudly: nobody logged in, so any action here is
            // attributed to a bootstrapped admin rather than to a person.
            <Badge tone="amber">dev bypass — no real login</Badge>
          ) : (
            // Only with a real session: there is no bypass to sign out of.
            <LogoutButton />
          )}
        </div>
      }
    >
      <nav className="flex gap-1">
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <button
              key={t.id}
              type="button"
              // Which tab you are on was said by the background colour and by
              // nothing else, so a screen reader announced three identical buttons.
              // The condition is the same one the styling uses — opening somebody
              // from the list leaves no tab current, which is already how it looks.
              aria-current={tab === t.id && !openId ? "page" : undefined}
              onClick={() => {
                setTab(t.id);
                setOpenId(null);
              }}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium",
                tab === t.id && !openId
                  ? "bg-slate-900 text-white"
                  : "text-slate-700 hover:bg-slate-100"
              )}
            >
              {t.label}
            </button>
          ))}
      </nav>

      <div className="mt-4">
        {openId ? (
          <IdentityDetail id={openId} viewer={viewer} onBack={() => setOpenId(null)} />
        ) : tab === "people" ? (
          <Identities
            viewer={viewer}
            view={peopleView}
            onView={setPeopleView}
            onOpen={setOpenId}
            onAddPerson={() => setAdding({ atOrganization: null })}
          />
        ) : tab === "orgs" ? (
          // Given the same opener as the People list, so an expanded roster leads to
          // the record: the headcount exists so somebody can check it, and checking
          // means reading the person. Going back lands on this tab, because the tab
          // is what it was opened from.
          <PartnerOrgs
            viewer={viewer}
            view={orgsView}
            onView={setOrgsView}
            onOpen={setOpenId}
            onAddPerson={(organization) => setAdding({ atOrganization: organization })}
          />
        ) : tab === "departments" ? (
          <Departments viewer={viewer} />
        ) : tab === "import" ? (
          <BulkImport viewer={viewer} />
        ) : tab === "okta" ? (
          <OktaExport viewer={viewer} />
        ) : (
          <AdminUsers viewer={viewer} />
        )}
      </div>

      {/* Closed on success, leaving the operator where they were. Every identity
          mutation invalidates the listing, so the person just added is already in the
          table behind the panel — a better answer than the old page's jump to their
          detail record. Unmounting on close is also what clears the form, so
          reopening starts empty. */}
      {adding ? (
        <FloatingPanel
          className="w-[min(72rem,calc(100vw-2rem))]"
          // Keyed on the firm so asking for a second one restarts the panel rather
          // than re-labelling it. Without this the operator would get a header naming
          // the firm they just clicked over a picker still holding the previous one —
          // a wrong write with nothing on screen to reveal it. It costs the draft,
          // which is the right trade here and only here: this is somebody explicitly
          // saying "no, that firm", unlike the browsing the panel was built to survive.
          //
          // It also has to be on the panel rather than on the form inside it.
          // `returnFocusTo` is captured by an effect with empty deps, so a panel that
          // merely re-rendered would keep sending focus to the first firm's button.
          key={adding.atOrganization?.id ?? "any"}
          title={
            adding.atOrganization
              ? `Add an existing person to ${adding.atOrganization.name}`
              : "Add a person"
          }
          onClose={() => setAdding(null)}
          // Looked up at close rather than captured at open: browsing to a
          // person and back unmounts the People list, so the button that opened
          // this is a different element by then.
          //
          // A distinct attribute per row rather than one shared with the People
          // list's button: `querySelector` returns the first match in document
          // order, so a shared one would land focus on whichever row sits highest.
          returnFocusTo={() =>
            document.querySelector<HTMLElement>(
              adding.atOrganization
                ? `[data-add-person-at="${adding.atOrganization.id}"]`
                : "[data-add-person]"
            )
          }
        >
          {adding.atOrganization ? (
            <AddOrgPerson
              viewer={viewer}
              organization={adding.atOrganization}
              onAdded={() => setAdding(null)}
            />
          ) : (
            <CreateIdentity viewer={viewer} onCreated={() => setAdding(null)} />
          )}
        </FloatingPanel>
      ) : null}
    </Shell>
  );
}

function Shell({ header, children }: { header?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Toaster position="top-right" />
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-3">
          <div>
            <h1 className="text-base font-semibold">
              Partner database / On-demand contractor database
            </h1>
            <p className="text-xs text-slate-500">
              Systems of record for partners and on-demand contractors
            </p>
          </div>
          {header}
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-5">{children}</main>
    </div>
  );
}

function LogoutButton() {
  return (
    <Button
      variant="secondary"
      onClick={() => {
        window.location.href = "/auth/logout";
      }}
    >
      Sign out
    </Button>
  );
}
